import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthenticatedApiRequest from "../AuthenticatedApiRequest";
import TchrURL from "../TchrURL";
import { UserSettings } from "../types/user";
import { toast } from "@/hooks/use-toast";
import { A11ySettings, translateLanguageShortCodeToName } from "../contexts/A11yProvider";
import { useTranslation } from "react-i18next";

interface AccessibilityUpdatePayload {
  displayLanguage?: string;
  preferences: {
    darkMode: boolean;
    highContrast: boolean;
    lowAnimation: boolean;
    dyslexiaFont: boolean;
    largeText: boolean;
  };
}

const useAccessibilitySettings = () => {
  const { patch } = useAuthenticatedApiRequest();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  const updateAccessibilitySettings = async (
    settings: A11ySettings
  ): Promise<UserSettings> => {
    const url = new TchrURL("/user/settings");

    const languageName = translateLanguageShortCodeToName(settings.language);
    const payload: AccessibilityUpdatePayload = {
      ...(languageName && { displayLanguage: languageName.toLowerCase() }),
      preferences: {
        darkMode: settings.darkMode,
        highContrast: settings.highContrast,
        lowAnimation: settings.lowAnimation,
        dyslexiaFont: settings.dyslexiaFont,
        largeText: settings.largeText,
      },
    };

    const response = await patch(url, JSON.stringify(payload));
    return response.json();
  };

  const useUpdateAccessibilitySettings = () =>
    useMutation({
      mutationKey: ["user", "settings", "accessibility"],
      mutationFn: updateAccessibilitySettings,
      onSuccess: () => {
        toast({
          title: t("toast.accessibilitySettings.success.title"),
          description: t("toast.accessibilitySettings.success.description"),
        });
        queryClient.invalidateQueries({ queryKey: ["user", "settings"] });
        queryClient.refetchQueries({ queryKey: ["user"] });
      },
      onError: (error) => {
        console.error("Failed to update accessibility settings:", error);
        toast({
          variant: "destructive",
          title: t("toast.accessibilitySettings.error.title"),
          description: t("toast.accessibilitySettings.error.description"),
        });
      },
    });

  return { useUpdateAccessibilitySettings };
};

export default useAccessibilitySettings;
